/**
 * Everything that turns one assistant part into DOM: markdown (marked as a
 * LEXER only -- tokens become React elements, nothing is ever innerHTML'd),
 * the chart fence, tables, tool traces, and the canvas panel. Imported by
 * chat.jsx; bundled into chat.js by scripts/build_static.sh.
 *
 * Token shapes are marked@15.0.12's, measured 2026-09-05: block tokens carry
 * `tokens` (inline children) or `items` (lists); a fenced block is
 * {type:'code', lang, text, raw}; an UNCLOSED fence lexes to the same token
 * running to end of input, which is why isClosedFence reads `raw`.
 *
 * Spec: docs/superpowers/specs/2026-09-05-chat-charts-history-design.md §5.
 */
import { isClosedFence, validateChartSpec, isNumericCell, safeHref, autoDir } from './chat-blocks.mjs';
import { sqlTokens, sqlSlices } from './workbench-logic.mjs';
import { QueryResult } from './chat-results.jsx';
import { validResult, canvasArtifact, prescribedRows, prescriptionNote, drawnMarks, keepsChatChart } from './chat-results.mjs';
import { RankedBars, TrendLine, Donut, StackedBars } from './bi-charts.jsx';
import { KpiTile, PivotTable, FlatTable, pivotize } from './bi-tables.jsx';

const { useRef, useEffect } = React;

export const chatCls = (...xs) => xs.filter(Boolean).join(' ');

// The chart module's categorical hues, drawn from the desk's inks (ballpoint
// blue, folder ochre, form green ...) and never the reserved stamp violet or
// void red. Fixed order (never cycled -- MAX_SERIES caps series at this array's
// own length, so the `%` below is defensive, not an actual wraparound). Checked
// 2026-09-27 with the dataviz skill's scripts/validate_palette.js against the
// sheet surface --card (#fdfcf8): lightness, chroma, normal-vision floor and
// contrast (all >= 3:1) PASS; CVD separation WARNs at dE 6.3 (protan) on the
// ochre/green pair, whose relief is the same as before: the always-on tooltip
// and, for >=2 series, the legend, so identity is never color alone.
const PALETTE = ['#2f5fb3', '#c47d12', '#1d8a55', '#5b8de0', '#b04f78', '#7f8c12', '#0a8fb0', '#b3582a'];
// Axis, legend and tooltip text in the page's own UI face, not Chart.js's Helvetica.
if (window.Chart) window.Chart.defaults.font.family = "'Heebo', Arial, sans-serif";

// marked hands inline text through VERBATIM (its `escaped` flag only means
// "inside an inline <pre>/<code> pair", measured on 15.0.12); its own HTML
// renderer never double-encodes an entity, so `Fish &amp; Chips` reads as
// `&` in every other markdown viewer. Decode here so it does on this page
// too -- Priority help carries &nbsp;, &quot; and numeric entities and the
// model echoes it. Bidi embedding/override/isolate controls are stripped as
// well: dir="auto" isolates direction and an override can only disguise a
// link. Marks (U+200E/F) are left alone.
const NAMED = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0' };
const BIDI_CTL = /[\u202A-\u202E\u2066-\u2069]/g;
// The strip half of cleanText, on its own: for text that must stay
// byte-literal otherwise (codespans) or has no entities to decode (image
// alt text, inline html source) but can still carry a bidi override -- a
// link's label can be a codespan or an image, so this has to run there too
// or the anti-spoofing property only half-applies.
const stripBidi = (s) => String(s ?? '').replace(BIDI_CTL, '');
// For code, SQL and raw source, deleting a bidi control is the wrong move:
// this page's own note text promises "every SQL query the assistant runs is
// shown in full," and JSON.stringify does not escape these controls (they
// pass straight through into a tool-args dump), so a silent strip would let
// the displayed query stop matching the bytes that actually ran -- the
// opposite of what showing it is for. Render each control as a visible
// token instead. Keys are the same nine \uXXXX escapes BIDI_CTL matches,
// spelled out rather than pasted as literal characters, for the same reason
// BIDI_CTL itself is: an invisible control belongs in source as text, not
// as a byte.
const BIDI_NAMES = {
  '\u202A': 'LRE', '\u202B': 'RLE', '\u202C': 'PDF', '\u202D': 'LRO', '\u202E': 'RLO',
  '\u2066': 'LRI', '\u2067': 'RLI', '\u2068': 'FSI', '\u2069': 'PDI',
};
const showBidi = (s) => String(s ?? '').replace(BIDI_CTL, (c) => `<${BIDI_NAMES[c]}>`);
// The workbench's SQL colouring (the .sq-* inks), over the text as SHOWN: the
// bidi names are in it, so the ranges line up with what is on screen.
export function SqlInk({ text }) {
  return sqlSlices(text, sqlTokens(text), 0, text.length).map((p, k) => (p.cls
    ? <span key={k} className={`sq-${p.cls}`}>{p.text}</span>
    : <React.Fragment key={k}>{p.text}</React.Fragment>));
}
// autoDir (dir="auto" does not work on a container whose text lives in
// dir-carrying children, and containers need it computed by hand instead)
// lives in chat-blocks.mjs, imported above -- pure text logic with no React
// or DOM, so it can run under `node --test`, which is also where the
// backtick-length and Unicode-property-escape reasoning lives now.
const cleanText = (s) => String(s)
  .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e) => {
    if (e[0] === '#') {
      const cp = (e[1] === 'x' || e[1] === 'X') ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(cp) && cp > 0 && cp < 0x110000 ? String.fromCodePoint(cp) : m;
    }
    return NAMED[e.toLowerCase()] ?? m;
  })
  .replace(BIDI_CTL, '');

function lex(text) {
  if (window.marked && typeof window.marked.lexer === 'function') {
    try {
      return window.marked.lexer(text || '');
    } catch (e) {
      // marked's own lexer throws on some inputs (e.g. "Infinite loop on
      // byte: ..." without options.silent) -- fall through to the
      // one-paragraph degrade below instead of tearing down the chat
      // subtree this call sits inside of.
    }
  }
  // marked failed to load, or its lexer threw: degrade to one paragraph, never to a blank.
  return [{ type: 'paragraph', raw: text || '', tokens: [{ type: 'text', text: text || '', escaped: false }] }];
}

export function blockAt(text, idx) {
  return lex(text)[idx];
}

// ---------- inline ----------
function Inline({ tokens }) {
  return (
    <>
      {(tokens || []).map((t, i) => {
        switch (t.type) {
          case 'text':
            return <React.Fragment key={i}>{t.tokens ? <Inline tokens={t.tokens}/> : cleanText(t.text)}</React.Fragment>;
          case 'escape':
            return <React.Fragment key={i}>{t.text}</React.Fragment>;
          case 'strong': return <strong key={i}><Inline tokens={t.tokens}/></strong>;
          case 'em': return <em key={i}><Inline tokens={t.tokens}/></em>;
          case 'del': return <del key={i}><Inline tokens={t.tokens}/></del>;
          case 'codespan': return <code key={i} className="mono chat-codespan" dir="ltr">{stripBidi(t.text)}</code>;
          case 'br': return <br key={i}/>;
          case 'link': {
            const href = safeHref(t.href);
            return href
              ? <a key={i} href={href} target="_blank" rel="noopener noreferrer"><Inline tokens={t.tokens}/></a>
              : <React.Fragment key={i}><Inline tokens={t.tokens}/></React.Fragment>;
          }
          case 'image': return <span key={i} dir="auto">{stripBidi(t.text || t.raw)}</span>;
          default:
            // Inline html and anything unmapped: its source, as text. Visible, never executed.
            return <React.Fragment key={i}>{stripBidi(t.raw)}</React.Fragment>;
        }
      })}
    </>
  );
}

// ---------- blocks ----------
function Blocks({ tokens, streaming, lang, onOpen }) {
  return (
    <>
      {(tokens || []).map((tok, i) => (
        <Block tok={tok} idx={i} streaming={streaming} lang={lang} onOpen={onOpen} key={i}/>
      ))}
    </>
  );
}

function Block({ tok, idx, streaming, lang, onOpen }) {
  const open = onOpen ? () => onOpen(idx) : null;
  switch (tok.type) {
    case 'space': return null;
    case 'heading':
      return <p className={`chat-h chat-h${Math.min(3, tok.depth)}`} dir="auto"><Inline tokens={tok.tokens}/></p>;
    case 'paragraph':
      return <p className="chat-msg-text" dir="auto"><Inline tokens={tok.tokens}/></p>;
    case 'text':
      return <p className="chat-msg-text" dir="auto">{tok.tokens ? <Inline tokens={tok.tokens}/> : tok.text}</p>;
    case 'list': {
      const L = tok.ordered ? 'ol' : 'ul';
      // The <li> carries no dir of its own: it would hit the same skip rule
      // (its text sits inside a dir="auto" paragraph), resolve LTR, and drag
      // the marker back to the left of an RTL list. Inheriting the list's
      // resolved direction is also what lines every marker up on one side;
      // each item's own text still resolves independently, in that paragraph.
      return (
        <L className="chat-list" dir={autoDir(tok.raw)} start={tok.ordered && Number.isInteger(tok.start) ? tok.start : undefined}>
          {tok.items.map((it, i) => (
            <li key={i}><Blocks tokens={it.tokens} streaming={streaming} lang={lang}/></li>
          ))}
        </L>
      );
    }
    case 'blockquote':
      return <blockquote className="chat-quote" dir={autoDir(tok.raw)}><Blocks tokens={tok.tokens} streaming={streaming} lang={lang}/></blockquote>;
    case 'hr': return <hr className="chat-hr"/>;
    case 'code':
      return tok.lang === 'chart'
        ? <ChartFence tok={tok} streaming={streaming} lang={lang} onOpen={open}/>
        : <pre className="chat-code mono" dir="ltr"><code>{showBidi(tok.text)}</code></pre>;
    case 'table':
      return <ChatTable header={tok.header} rows={tok.rows} lang={lang} onOpen={open}/>;
    case 'html':
      return <p className="chat-msg-text" dir="auto">{stripBidi(tok.text)}</p>;
    default:
      return <p className="chat-msg-text" dir="auto">{stripBidi(tok.raw)}</p>;
  }
}

// Memoized: lex() re-parses the whole message text on every call, and an
// unmemoized component re-renders (so re-lexes) on every render of its
// parent -- the same shape of bug the chart fix above already addresses for
// ChatChart, one level up. For the memo to actually hit, every prop must
// compare equal across renders -- so the contract takes `turnIdx`/`partIdx`
// (primitives, trivially stable) and composes the block-open call
// internally, rather than taking a ready-made per-message `onOpen` closure
// (which a caller would have to rebuild inside a .map(), where a hook can't
// legally live, defeating the memo every render). `onOpen` itself is then
// one stable callback the whole page shares, called as
// `onOpen({ turnIdx, partIdx, blockIdx })`.
export const ChatMarkdown = React.memo(function ChatMarkdown({ text, streaming, lang, onOpen, turnIdx, partIdx }) {
  const open = onOpen ? (blockIdx) => onOpen({ turnIdx, partIdx, blockIdx }) : undefined;
  return <Blocks tokens={lex(text)} streaming={streaming} lang={lang} onOpen={open}/>;
});

// ---------- chart ----------
function ChartError({ reason, raw, lang }) {
  const he = lang === 'he';
  return (
    <div className="chat-figure-err" dir="auto">
      {he ? `לא ניתן לצייר את התרשים: ${reason}` : `Chart could not be drawn: ${reason}`}
      <details><summary>{he ? 'הצג את הבלוק' : 'Show the block'}</summary><pre dir="ltr">{showBidi(raw)}</pre></details>
    </div>
  );
}

function parseFence(tok) {
  let parsed;
  try { parsed = JSON.parse(tok.text); } catch (e) { return { ok: false, reason: `invalid JSON: ${e.message}` }; }
  return validateChartSpec(parsed);
}

function ChartFence({ tok, streaming, lang, onOpen }) {
  const he = lang === 'he';
  if (!isClosedFence(tok.raw)) {
    if (streaming) return <div className="chat-figure chat-figure-wait" dir="auto">{he ? 'מכין תרשים…' : 'Preparing chart…'}</div>;
    return <ChartError reason={he ? 'הבלוק לא נסגר' : 'the chart block was never closed'} raw={tok.text} lang={lang}/>;
  }
  const v = parseFence(tok);
  if (!v.ok) return <ChartError reason={v.reason} raw={tok.text} lang={lang}/>;
  return (
    <figure className="chat-figure">
      <figcaption className="chat-figure-head">
        <span dir="auto">{stripBidi(v.spec.title)}</span>
        {onOpen && <button type="button" className="btn btn-sm" onClick={onOpen}>{he ? 'פתח' : 'Open'}</button>}
      </figcaption>
      {/* Keyed on the JSON, so a delta elsewhere in the message re-renders prose, not the chart. */}
      <ChatChart spec={v.spec} lang={lang} height={280} key={tok.text}/>
    </figure>
  );
}

// Reads a colour custom property off the document root. Canvas fillStyle
// cannot resolve var(...) itself -- unlike a DOM element's inline style, a
// Chart.js config needs the resolved string -- so chart chrome (axis/legend
// text and gridlines) is tied to the page's own tokens this way instead of a
// hardcoded chart-only gray. Called at chart-build time (on mount / spec
// change, never per keystroke -- see ChatChart's effect), so the page's
// stylesheet has always finished loading by then.
function cssVar(name, fallback) {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name);
  return v && v.trim() ? v.trim() : fallback;
}

function chartConfig(spec, lang) {
  const rtl = lang === 'he';
  const circular = spec.type === 'pie' || spec.type === 'doughnut';
  const datasets = spec.series.map((s, i) => ({
    label: s.name,
    data: spec.type === 'scatter' ? s.values.map(([x, y]) => ({ x, y })) : s.values,
    backgroundColor: circular ? spec.labels.map((_, j) => PALETTE[j % PALETTE.length]) : PALETTE[i % PALETTE.length],
    borderColor: circular ? '#fff' : PALETTE[i % PALETTE.length],
    borderWidth: spec.type === 'line' ? 2 : 1,
    fill: false,
    tension: 0.25,
  }));
  // Axis/legend text never wears a series color (marks-and-anatomy.md) --
  // it rides the page's own muted-ink and hairline-grid tokens instead of
  // Chart.js's built-in dark-gray defaults, which read too heavy against
  // the paper surface.
  const axisText = cssVar('--ink-3', '#6b655c');
  const axisGrid = cssVar('--line-soft', '#f0ece5');
  return {
    type: spec.type,
    data: { labels: spec.labels || undefined, datasets },
    options: {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      locale: rtl ? 'he-IL' : 'en-US',
      plugins: {
        legend: {
          display: circular || spec.series.length > 1,
          rtl,
          textDirection: rtl ? 'rtl' : 'ltr',
          labels: { color: axisText },
        },
        tooltip: { rtl, textDirection: rtl ? 'rtl' : 'ltr' },
      },
      scales: circular ? {} : {
        x: {
          stacked: spec.stacked,
          title: { display: !!spec.x_label, text: spec.x_label, color: axisText },
          grid: { color: axisGrid },
          ticks: { color: axisText },
        },
        y: {
          stacked: spec.stacked,
          beginAtZero: true,
          title: { display: !!spec.y_label, text: spec.y_label, color: axisText },
          grid: { color: axisGrid },
          ticks: { color: axisText },
        },
      },
    },
  };
}

export function ChatChart({ spec, lang, height, showValues = true }) {
  const ref = useRef(null);
  // Every parent render hands down a NEW spec object (the fence is re-lexed
  // and re-parsed per render), so the effect keys on the JSON, not the
  // reference. Keyed on the reference it rebuilt every chart on the page on
  // every keystroke in the composer -- measured in the plan review: 90
  // constructions in a short session, 1 with this.
  const specJson = JSON.stringify(spec);
  useEffect(() => {
    if (!ref.current || !window.Chart) return undefined;
    const chart = new window.Chart(ref.current, chartConfig(JSON.parse(specJson), lang));
    return () => chart.destroy();
  }, [specJson, lang]);
  const he = lang === 'he';
  const scatter = spec.type === 'scatter';
  return (
    <div className="chat-chart">
      {/* aria-label carries the accessible name (canvas has no text content
          a screen reader can read); the child is the fallback a browser
          without canvas support renders instead. spec.title is already
          validated and length-capped by validateChartSpec. */}
      {window.Chart ? <div className="chat-chart-plot" style={{ height }}><canvas ref={ref} role="img" aria-label={stripBidi(spec.title)}>{stripBidi(spec.title)}</canvas></div>
        : <p className="chat-figure-err">{he ? 'ספריית התרשימים לא נטענה' : 'The chart library did not load'}</p>}
      {showValues && <details className="chat-chart-values">
        <summary>{he ? 'ערכי התרשים' : 'Chart values'}</summary>
        <div className="tbl-wrap" tabIndex={0} role="region" aria-label={stripBidi(spec.title)}>
          <table className="tbl">
            <caption dir="auto">{stripBidi(spec.title)} · {he ? '? = ערך לא זמין' : '? = unavailable value'}</caption>
            <thead><tr><th scope="col">{he ? 'סדרה' : 'Series'}</th><th scope="col" dir="auto">{spec.x_label || (scatter ? 'X' : he ? 'קטגוריה' : 'Category')}</th><th scope="col" dir="auto">{spec.y_label || (scatter ? 'Y' : he ? 'ערך' : 'Value')}</th></tr></thead>
            <tbody>{spec.series.flatMap((series, si) => series.values.map((value, vi) => <tr key={`${si}-${vi}`}>
              <th scope="row" dir="auto">{stripBidi(series.name)}</th>
              <td dir="auto">{scatter ? value[0] : stripBidi(spec.labels[vi])}</td>
              <td>{(scatter ? value[1] : value) ?? '?'}</td>
            </tr>))}</tbody>
          </table>
        </div>
      </details>}
    </div>
  );
}

// ---------- table ----------
export function ChatTable({ header, rows, lang, onOpen, tall }) {
  const he = lang === 'he';
  // navigator.clipboard exists only in a secure context (https or localhost);
  // over plain http on the LAN the button would be silently inert, so it is
  // not shown at all there.
  const canCopy = typeof navigator !== 'undefined' && !!navigator.clipboard;
  const copy = () => {
    const tsv = [header.map((c) => c.text), ...rows.map((r) => r.map((c) => c.text))]
      .map((r) => r.join('\t')).join('\n');
    navigator.clipboard.writeText(tsv).catch(() => {});
  };
  return (
    <div className={chatCls('tbl-wrap chat-result', tall && 'chat-result-tall')}>
      <div className="chat-result-head">
        <span className="chat-result-count" dir="auto">{rows.length} {he ? 'שורות' : 'rows'}</span>
        {canCopy && <button type="button" className="btn btn-sm" onClick={copy}>{he ? 'העתק' : 'Copy'}</button>}
        {onOpen && <button type="button" className="btn btn-sm" onClick={onOpen}>{he ? 'פתח' : 'Open'}</button>}
      </div>
      <table className="tbl">
        <thead>
          <tr>{header.map((h, k) => <th key={k} dir="auto" className={isNumericCell(h.text) ? 'num' : undefined}><Inline tokens={h.tokens}/></th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci} dir="auto" className={isNumericCell(c.text) ? 'num' : undefined}><Inline tokens={c.tokens}/></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- tool trace (moved from chat.jsx, unchanged) ----------
function toolTrace(tool) {
  if (tool.name === 'search') {
    // showBidi, not stripBidi: same fidelity argument as the sql branch --
    // this is the verbatim record of what the tool was called with.
    return <span dir="auto">"{showBidi(tool.args && tool.args.q)}"</span>;
  }
  if (tool.name === 'sql') {
    // showBidi, not stripBidi: this is the actually-executed query -- the
    // page's own promise is that it is shown in full, so a control here
    // must stay visible, not vanish.
    const q = showBidi(((tool.args && tool.args.query) || '').replace(/\s+/g, ' ').trim());
    return <span className="mono" dir="auto">{q.length > 70 ? `${q.slice(0, 70)}…` : q}</span>;
  }
  // Every tool other than search/sql lands here (describe, lookup_value,
  // recall, remember) -- the common case in this summary line, not an edge
  // one. Same fidelity argument, same showBidi.
  return <span dir="auto">{showBidi(JSON.stringify(tool.args))}</span>;
}

// The database's own sentence out of a driver error: asyncpg's "<class '...'>: column x
// does not exist" becomes "column x does not exist". ponytail: 120 chars, the summary is
// one line; the whole error renders in the body below.
const errorGist = (s) => {
  const text = String(s);
  const m = /<class '[^']+'>:\s*([^\n]+)/.exec(text);
  const line = (m ? m[1] : text.split('\n')[0]).trim();
  return line.length > 120 ? `${line.slice(0, 120)}…` : line;
};

export function ChatTool({ tool, lang }) {
  const he = lang === 'he';
  const args = tool.args && typeof tool.args === 'object' ? tool.args : {};
  const query = typeof args.query === 'string' ? args.query : null;
  const rest = Object.entries(args).filter(([k]) => !(k === 'query' && query !== null));
  return (
    <details className={chatCls('chat-tool', tool.error && 'chat-tool-err')} dir="ltr">
      <summary className="mono">
        <span className="chat-tool-name">{tool.name}</span>({toolTrace(tool)})
        {/* The summary carries the database's own message; the whole error, like the
            whole query, is one click away in the body below. */}
        {/* Its own isolated run in the page's direction, so the label reads first
            and the message never runs into the query's closing parenthesis. */}
        {tool.error && (
          <span className="chat-tool-errmsg" dir={he ? 'rtl' : 'ltr'}>
            <b>{he ? 'שגיאה' : 'error'}:</b> <bdi>{showBidi(errorGist(tool.error))}</bdi>
          </span>
        )}
      </summary>
      {/* The query in full, as it ran: its own line breaks, never JSON-escaped. */}
      {query !== null && <pre className="chat-tool-sql" dir="ltr"><SqlInk text={showBidi(query)}/></pre>}
      {rest.length > 0 && (
        <dl className="chat-tool-kv">
          {rest.map(([k, v]) => (
            <div key={k}><dt>{k}</dt><dd dir="auto">{showBidi(typeof v === 'string' ? v : JSON.stringify(v))}</dd></div>
          ))}
        </dl>
      )}
      {tool.error && <pre className="chat-tool-args chat-tool-errmsg" dir="auto">{showBidi(tool.error)}</pre>}
    </details>
  );
}

// ---------- canvas ----------
export function Canvas({ title, lang, onClose, children }) {
  const closeRef = useRef(null);
  useEffect(() => {
    const trigger = document.activeElement;
    closeRef.current?.focus();
    return () => { if (trigger?.isConnected) trigger.focus(); };
  }, []);
  return (
    <aside className="card chat-canvas">
      <div className="chat-canvas-head">
        <span dir="auto">{stripBidi(title)}</span>
        <button ref={closeRef} type="button" className="btn btn-sm" onClick={onClose} aria-label={lang === 'he' ? 'סגור' : 'Close'}>×</button>
      </div>
      <div className="chat-canvas-body">{children}</div>
    </aside>
  );
}

// The BI components, for the browser fixtures (tests/browser/test_bi_visuals.py, test_bi_tables.py).
window.pmBi = { RankedBars, TrendLine, Donut, StackedBars, KpiTile, PivotTable, FlatTable, pivotize };

// A chart the server's doctor prescribed (spec.prescription: {mark, horizontal, top, rest, notes})
// draws with the BI components; any other mark, or no prescription, stays with ChatChart.
// extra (the report page only): {onPick, selected} for a donut's cross-filter, {compare: Map(label -> value),
// names} for a trend's previous period, {order} the entities' kept order so a colour follows its entity.
function biChart(spec, lang, height, extra = {}) {
  const { mark } = spec.prescription;
  if (spec.series.length > 1 && mark !== 'stacked') return null; // only series[0] is mapped below; ChatChart draws them all
  const h = typeof height === 'number' ? height : 360;
  if (!drawnMarks.has(mark) || keepsChatChart(spec)) return null;
  const rows = prescribedRows(spec);
  if (mark === 'bar') return <RankedBars rows={rows} label="label" value="value" lang={lang}/>;
  if (mark === 'line') {
    return <TrendLine points={rows.map((r) => ({ x: r.label, y: r.value }))} reference="average" lang={lang} height={h}
      comparePoints={extra.compare ? rows.map((r) => ({ x: r.label, y: extra.compare.get(r.label) ?? null })) : null} names={extra.names}/>;
  }
  if (mark === 'donut') return <Donut slices={rows.map((r) => ({ key: r.label, label: r.label, value: r.value }))} lang={lang} height={h} onPick={extra.onPick} selected={extra.selected ?? null} order={extra.order}/>;
  if (mark === 'stacked') {
    const stacked = spec.labels.map((x, i) => Object.fromEntries([['x', x], ...spec.series.map((s) => [s.name, s.values[i]])]));
    return <StackedBars rows={stacked} x="x" series={spec.series.map((s) => s.name)} lang={lang} height={h} order={extra.order}/>;
  }
  return null;
}

// One block, canvas-sized. Same validator, same table component -- only the size differs.
export const renderResultChart = (spec, lang, height, extra) => {
  const bi = spec.prescription && biChart(spec, lang, height, extra);
  const note = bi && prescriptionNote(spec);
  return bi ? <><div className="chat-chart">{bi}</div>{note && <p className="bi-foot" dir="auto" data-prescription-note>{note}</p>}</> : <ChatChart spec={spec} lang={lang} height={height}/>;
};

export function CanvasBlock({ tok, lang }) {
  if (!tok) return null;
  const he = lang === 'he';
  if (tok.type === 'query-result') return <QueryResult key={`${tok.artifact.company}:${tok.artifact.captured_at}:${tok.artifact.query}`} artifact={tok.artifact} lang={lang} expanded autoChart={false} renderChart={renderResultChart}/>;
  if (tok.type === 'code' && tok.lang === 'chart') {
    const v = isClosedFence(tok.raw)
      ? parseFence(tok)
      : { ok: false, reason: he ? 'הבלוק לא נסגר' : 'the chart block was never closed' };
    return v.ok ? <ChatChart spec={v.spec} lang={lang} height="100%" key={tok.text}/> : <ChartError reason={v.reason} raw={tok.text} lang={lang}/>;
  }
  if (tok.type === 'table') return <ChatTable header={tok.header} rows={tok.rows} lang={lang} tall/>;
  return null;
}

export function canvasTitle(tok, lang) {
  if (tok?.type === 'query-result') return tok.artifact.title;
  if (tok && tok.type === 'code' && tok.lang === 'chart') {
    const v = isClosedFence(tok.raw) ? parseFence(tok) : { ok: false };
    if (v.ok) return v.spec.title;
  }
  return lang === 'he' ? 'טבלה' : 'Table';
}

// ---------- notices and one turn: moved verbatim from chat.jsx (studio follow-up spec §5.3),
// so the Studio's Ask box renders a turn exactly as the chat page does ----------
export function ChatStreamError({ message, lang }) {
  return (
    <div className="chat-notice" dir="auto">
      {lang === 'he' ? `התשובה נעצרה: ${message}` : `The answer stopped: ${message}`}
    </div>
  );
}

export function ChatNotice({ notice, lang, role, onOpenSettings }) {
  if (notice.kind === 'nokey') {
    return (
      <div className="card chat-nokey">
        <div className="chat-nokey-title">{lang === 'he' ? 'לא הוגדר מפתח LLM' : 'No LLM key is set'}</div>
        <div className="chat-nokey-detail" dir="auto">{notice.detail}</div>
        {role === 'admin' ? (
          <button type="button" className="btn btn-sm" onClick={onOpenSettings}>
            {lang === 'he' ? 'עבור להגדרות' : 'Go to settings'}
          </button>
        ) : (
          <div className="fv-note">
            {lang === 'he' ? 'בקשו ממנהל להוסיף מפתח בעמוד ההגדרות.' : 'Ask an admin to add one on the settings page.'}
          </div>
        )}
      </div>
    );
  }
  return <div className="chat-notice" dir="auto">{notice.detail}</div>;
}

// ---------- one turn ----------
// Memoized: every SSE delta replaces the `turns` array wholesale, so without
// this every prior message re-renders on every delta of the streaming one.
// Older turn objects keep their identity across those replacements, so the
// memo actually hits; only the streaming turn's own object changes. Props
// are the stable `setCanvas` setter (passed as `onOpen`), the turn object,
// and primitives.
export const ChatMessage = /* @__PURE__ */ React.memo(function ChatMessage({ turn, turnIdx, lang, role, onOpenSettings, onOpen, onPin }) {
  if (turn.role === 'notice') {
    return <ChatNotice notice={turn} lang={lang} role={role} onOpenSettings={onOpenSettings}/>;
  }
  const he = lang === 'he';
  const isUser = turn.role === 'user';
  return (
    <div className={chatCls('chat-msg', isUser ? 'user' : 'assistant', turn.streaming && 'live')}>
      <span className={chatCls('chat-av', !isUser && 'assistant')} aria-hidden="true">
        {isUser ? (he ? 'א' : 'Y') : 'P'}
      </span>
      <div className="chat-msg-body">
        <div className="chat-msg-role">{isUser ? (he ? 'אתה' : 'You') : (he ? 'עוזר' : 'Assistant')}</div>
        {isUser && <p className="chat-msg-text" dir="auto">{turn.text}</p>}
        {/* Array.isArray, not `|| []`: a stored turn of the wrong shape must not blank the page for the next login that selects it. */}
        {!isUser && (Array.isArray(turn.parts) ? turn.parts : []).map((p, i) => {
          if (!p) return null;
          if (p.type === 'text') {
            return (
              <ChatMarkdown text={p.text} streaming={!!turn.pending || !!turn.streaming} lang={lang}
                            onOpen={onOpen} turnIdx={turnIdx} partIdx={i} key={i}/>
            );
          }
          if (p.type === 'tool') return <React.Fragment key={i}>
            <ChatTool tool={p} lang={lang}/>
            {validResult(p.artifact) && <QueryResult artifact={p.artifact} lang={lang} renderChart={renderResultChart}
              onPin={onPin}
              onOpen={onOpen ? selected => onOpen({ turnIdx, partIdx: i, artifact: true,
                presentation: canvasArtifact(selected) }) : null}/>}
            {p.save_unavailable === true && <p className="chat-notice" dir="auto">{he ? 'לא ניתן לשמור את התשובה הזו ללוח נתונים.' : 'This answer cannot be saved to a dashboard.'}</p>}
            {(p.artifact_error || (p.artifact && !validResult(p.artifact))) && <p className="chat-notice" dir="auto">
              {he ? 'לא ניתן להציג את תוצאת השאילתה.' : 'The query result could not be displayed.'}
              {typeof p.artifact_error === 'string' && <> {p.artifact_error}</>}
            </p>}
          </React.Fragment>;
          if (p.type === 'notice') return <p className="chat-msg-text muted" dir="auto" key={i}>{p.text}</p>;
          if (p.type === 'stream-error') return <ChatStreamError message={p.message} lang={lang} key={i}/>;
          return null;
        })}
        {!isUser && turn.stopped && <div className="chat-pending">{he ? 'נעצר' : 'Stopped'}</div>}
        {/* While the answer streams, a live line says the agent is still at it --
            between tool calls too, where nothing else on the page moves. */}
        {!isUser && (turn.pending || turn.streaming) && (
          <div className="chat-pending" role="status">
            {Array.isArray(turn.parts) && turn.parts.length ? (he ? 'עובד…' : 'Working…') : (he ? 'חושב…' : 'Thinking…')}
          </div>
        )}
      </div>
    </div>
  );
});
